import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useButton, useDispatch, useI18n, useLogger } from '~Hooks';
import { UpdateBookingTime, UpdateBookingTimeVariables } from './__queries__';
import { Box, Button, GreenButton, WhiteButton } from '~Styles';
import { GetBookings_operativeViewer_bookingsForVenueAndDate } from '~Providers/BookingsProvider/api/__queries__';
import Modal from '~ComponentsImproved/Modal';
import DateTimePicker from '~ComponentsImproved/DateTimePicker';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Loader from '~ComponentsImproved/Loader';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { DateTime } from 'luxon';
import * as actions from '~actions';
import { useBookings } from '~Providers/BookingsProvider';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';

const UPDATE_BOOKING_TIME = gql`
  mutation UpdateBookingTime($bookingId: ID!, $startTime: String!, $endTime: String!) {
    updateBookingTime(bookingId: $bookingId, startTime: $startTime, endTime: $endTime) {
      success
      message
      error
      booking {
        id
      }
    }
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ErrorBox = styled(Box)`
  background-color: color-mix(in srgb, var(--colorUIRed) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--colorUIRed) 30%, transparent);
  border-radius: 8px;
  padding: 10px 14px;
  text-align: center;
  animation: ${fadeIn} 0.2s ease-in-out forwards;
`;

const CancelButton = styled(Button)`
  max-width: 160px;
  white-space: nowrap;
`;

const PickerWrapper = styled(Box)`
  background-color: var(--colorBlueGray1100);
  border: 1px solid var(--colorBlueGray900);
  border-radius: 8px;
  padding: 8px;
`;

/** Helper to parse "HH:mm" into a Date object for the time picker */
const parseTimeToDate = (timeStr: string, quarterHour?: boolean): Date => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  if (quarterHour) {
    date.setMinutes(Math.floor(date.getMinutes() / 15) * 15, 0, 0);
  }
  return date;
};

interface Props {
  booking: GetBookings_operativeViewer_bookingsForVenueAndDate;
  onUpdated?: () => void;
}

const EditBookingTime: FC<Props> = ({ booking, onUpdated }) => {
  const { i18n } = useI18n();
  const logger = useLogger('edit-booking-time');
  const dispatch = useDispatch();
  const bookings = useBookings();
  const editModal = useModal();

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [startTimeISO, setStartTimeISO] = useState<string | null>(null);
  const [endTimeISO, setEndTimeISO] = useState<string | null>(null);

  const [updateBookingTime] = useMutation<UpdateBookingTime, UpdateBookingTimeVariables>(
    UPDATE_BOOKING_TIME,
  );

  const editButton = useButton({
    onClick: () => {
      setError(null);
      editModal.toggle();
    },
  });

  const cancelButton = useButton({
    onClick: editModal.toggle,
  });

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    // Use the picker values if changed, otherwise fall back to original booking times
    const startTime = startTimeISO
      ? DateTime.fromISO(startTimeISO).toFormat('HH:mm')
      : booking.startTime;
    const endTime = endTimeISO ? DateTime.fromISO(endTimeISO).toFormat('HH:mm') : booking.endTime;

    try {
      const res = await updateBookingTime({
        variables: {
          bookingId: booking.id,
          startTime,
          endTime,
        },
      });

      if (!res.data?.updateBookingTime?.success) {
        throw new Error(
          res.data?.updateBookingTime?.error ||
            res.data?.updateBookingTime?.message ||
            i18n('Bookings.ErrorUpdatingBookingTime'),
        );
      }

      logger.info('Booking time updated', {
        bookingId: booking.id,
        startTime,
        endTime,
      });

      dispatch(actions.addToast(i18n('Bookings.BookingTimeUpdated'), true));

      // Refetch bookings
      if (booking.date) {
        bookings.fetchBookingsForDate(booking.date);
      }

      onUpdated?.();
      editModal.toggle();
    } catch (err) {
      logger.error(`Failed to update booking time: ${err ? err.toString() : ''}`);
      setError(err ? err.toString() : i18n('Bookings.ErrorUpdatingBookingTime'));
    } finally {
      setLoading(false);
    }
  };

  const saveButton = useButton({
    onClick: handleSave,
    disabled: loading,
  });

  return (
    <>
      <WhiteButton {...editButton}>{i18n('Bookings.EditTime')}</WhiteButton>

      <Modal isShowing={editModal.isShowing} hide={editModal.toggle} backdropClose={true}>
        <ModalWrapper>
          <Box display="flex" gap="small" fluid>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Paragraph level={4} color="var(--colorBlueGray200)" bottom={8}>
                {i18n('Bookings.StartTime')}
              </Paragraph>
              <PickerWrapper fluid>
                <DateTimePicker
                  onChange={setStartTimeISO}
                  dark
                  fullWidth
                  timeOnly
                  quarterHourSteps
                  size="sm"
                  initialValue={parseTimeToDate(booking.startTime, true)}
                />
              </PickerWrapper>
            </Box>

            <Box display="flex" flexDirection="column" alignItems="center">
              <Paragraph level={4} color="var(--colorBlueGray200)" bottom={8}>
                {i18n('Bookings.EndTime')}
              </Paragraph>
              <PickerWrapper fluid>
                <DateTimePicker
                  onChange={setEndTimeISO}
                  dark
                  fullWidth
                  timeOnly
                  quarterHourSteps
                  size="sm"
                  initialValue={parseTimeToDate(booking.endTime, true)}
                />
              </PickerWrapper>
            </Box>
          </Box>

          <Box display="flex" flexDirection="row" gap="small" alignItems="center" fluid>
            <CancelButton {...cancelButton}>{i18n('General.Cancel')}</CancelButton>
            <GreenButton {...saveButton} fluid>
              {loading ? (
                <Loader size="sm" color="var(--colorWhite)" />
              ) : (
                i18n('Bookings.SaveTime')
              )}
            </GreenButton>
          </Box>

          {error && (
            <ErrorBox fluid display="flex" alignItems="center" justifyContent="center">
              <Paragraph level={5} bottom={0} color="var(--colorUIRed)">
                {error}
              </Paragraph>
            </ErrorBox>
          )}
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default EditBookingTime;
